/**
 * 合集详情页（只读视图）
 *
 * - 任何登录用户都可以访问 public 合集
 * - 私有合集仅 owner 可见
 * - 点击条目跳转到播放器（Phase 1：播放器不感知合集，只是普通播放）
 * - 顶部展示合集元信息 + 在 owner 模式下提供"编辑合集"入口
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { clearAuth, getAuthHeaders, getUser } from '../utils/auth';
import { resolveAssetUrl } from '../utils/assetUrl';
import { DEFAULT_COVER_PATH, handleCoverError } from '../utils/videoCover';
import type { ApiResponse } from '../types/api';
import type { CollectionDetail as CollectionDetailDTO } from '../types/collection';
import './CollectionDetail.css';

const getLanguageName = (langCode: string): string => {
  const map: Record<string, string> = {
    zh: '中文',
    en: '英语',
    ja: '日语',
    ko: '韩语',
    es: '西班牙语',
    fr: '法语',
    de: '德语',
    ru: '俄语',
  };
  return map[langCode] || langCode;
};

const formatDuration = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

const CollectionDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { collectionId } = useParams<{ collectionId: string }>();
  const [data, setData] = useState<CollectionDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUser = getUser();

  /**
   * 返回时优先用调用方在 navigate 时通过 `state.returnTo` 传入的路径；
   * 缺失时兜底回首页的"合集" tab（最常见的入口）。
   * 这样无论从首页、个人页还是某个视频卡片角标进来，返回路径都正确。
   */
  const returnTo = (location.state as { returnTo?: string } | null | undefined)?.returnTo;
  const handleBack = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate('/?tab=collections');
    }
  }, [navigate, returnTo]);

  const load = useCallback(async () => {
    if (!collectionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/collections/${collectionId}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      if (res.status === 404) {
        setError('合集不存在或已被删除');
        return;
      }
      if (res.status === 403) {
        setError('无权查看此合集');
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const payload = (await res.json()) as ApiResponse<CollectionDetailDTO>;
      if (payload.success && payload.data) {
        setData(payload.data);
      } else {
        setError(payload.error || '加载合集失败');
      }
    } catch (err) {
      console.error('[CollectionDetail] 加载失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [collectionId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePlay = (videoId: string) => {
    // 跳转到播放器；带上 collectionId 让播放器返回时回到当前合集页
    // 同时把 CollectionDetail 自己收到的 returnTo 透传给播放器，
    // 这样播放器返回 CollectionDetail 后，CollectionDetail 的返回按钮仍指向最初的来源
    if (!collectionId) return;
    const params = new URLSearchParams();
    params.set('video', videoId);
    params.set('from', 'collection');
    params.set('collectionId', collectionId);
    if (returnTo) params.set('returnTo', returnTo);
    navigate(`/player?${params.toString()}`);
  };

  if (loading) {
    return <div className="collection-detail-loading">加载中...</div>;
  }

  if (error) {
    return (
      <div className="collection-detail-error">
        <p>{error}</p>
        <button type="button" onClick={handleBack}>返回</button>
      </div>
    );
  }

  if (!data) return null;

  const isOwner = !!currentUser?.id && currentUser.id === data.userId;
  const heroCover = data.coverUrl
    ? resolveAssetUrl(data.coverUrl) || DEFAULT_COVER_PATH
    : (data.items[0]?.videoCoverUrl
        ? resolveAssetUrl(data.items[0].videoCoverUrl) || DEFAULT_COVER_PATH
        : DEFAULT_COVER_PATH);

  return (
    <div className="collection-detail-page">
      <div className="collection-detail-topbar">
        <button
          type="button"
          className="collection-detail-back"
          onClick={handleBack}
          aria-label="返回"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          <span>返回</span>
        </button>
      </div>

      <div className="collection-detail-hero">
        <div className="collection-detail-hero__cover">
          <img src={heroCover} alt={data.name} onError={handleCoverError} />
          <div className="thumbnail-placeholder" style={{ display: 'none' }}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
        </div>
        <div className="collection-detail-hero__meta">
          <div className="collection-detail-hero__badges">
            <span className={`collection-detail-badge ${data.visibility === 'public' ? 'is-public' : 'is-private'}`}>
              {data.visibility === 'public' ? '公开' : '私有'}
            </span>
            <span className="collection-detail-badge">{getLanguageName(data.sourceLanguage)}</span>
            <span className="collection-detail-badge">{data.items.length} 集</span>
          </div>
          <h1 className="collection-detail-hero__name">{data.name}</h1>
          {data.username && (
            <div className="collection-detail-hero__owner">@{data.username}</div>
          )}
          {data.description && (
            <p className="collection-detail-hero__desc">{data.description}</p>
          )}
          <div className="collection-detail-hero__actions">
            {data.items.length > 0 && (
              <button
                type="button"
                className="collection-detail-play-btn"
                onClick={() => {
                  const first = data.items[0];
                  if (first) handlePlay(first.videoId);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                <span>从头播放</span>
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                className="collection-detail-edit-btn"
                onClick={() => navigate(`/collections/${data.id}/edit`)}
              >
                编辑合集
              </button>
            )}
          </div>
        </div>
      </div>

      <ul className="collection-detail-items">
        {data.items.map((it) => (
          <li
            key={it.itemId}
            className="collection-detail-item"
            onClick={() => handlePlay(it.videoId)}
          >
            <div className="collection-detail-item__pos">{it.position}</div>
            <div className="collection-detail-item__thumb">
              <img
                src={it.videoCoverUrl ? resolveAssetUrl(it.videoCoverUrl) || DEFAULT_COVER_PATH : DEFAULT_COVER_PATH}
                alt={it.videoName || it.videoId}
                onError={handleCoverError}
              />
              {it.videoDurationSeconds ? (
                <div className="collection-detail-item__duration">
                  {formatDuration(it.videoDurationSeconds)}
                </div>
              ) : null}
            </div>
            <div className="collection-detail-item__meta">
              <div className="collection-detail-item__name">
                {it.videoName || it.videoId}
              </div>
              <div className="collection-detail-item__sub">
                {it.videoUsername && <span>@{it.videoUsername}</span>}
                {it.videoSourceLanguage && (
                  <span>· {getLanguageName(it.videoSourceLanguage)}</span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {data.items.length === 0 && (
        <div className="collection-detail-empty">
          <p>这个合集还没有视频。</p>
          {isOwner && (
            <button
              type="button"
              className="collection-detail-edit-btn"
              onClick={() => navigate(`/collections/${data.id}/edit`)}
            >
              添加视频
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CollectionDetail;
